import { classColor, PALETTE, paletteColor, VISUAL_ROLES, type VisualRole } from "@eldrun/content";
import { Color, Vector4, type IUniform, type ShaderMaterial } from "three";

/**
 * Papéis visuais da malha neon (F58/F59) como uniforms do trecho `style`: cinza e rim (`uStyleRole`),
 * cor e força do acento (`uStyleAccent`) e acento na peça inteira (`uStyleWhole`).
 */

/** Papéis cujo acento cobre a superfície inteira (não só a máscara `tint`). */
const WHOLE: ReadonlySet<VisualRole> = new Set(["arma-heroi", "nucleo-inimigo", "nucleo-chefe", "fx", "tesouro"]);

export type RoleUniforms = { uStyleRole: IUniform<Vector4>; uStyleAccent: IUniform<Vector4>; uStyleWhole: IUniform<number> };

/**
 * Modo daltônico (F60, opcional): as cores de jogo passam pela variante da paleta (`paletteColor`). Os
 * uniforms vivos ficam num registro fraco e são reescritos quando a opção muda, sem recriar material.
 */
let colorblind = false;
/**
 * `hex` ausente: o papel segue o acento do mundo (`setWorldAccent`). A referência fraca é ao uniform
 * `uStyleAccent` (o material o guarda), não ao objeto que `roleUniforms` devolve: quem espalha esse objeto
 * (`{ ...roleUniforms(...) }`) o solta, e o coletor apagava o registro antes do acento mudar (revisão da
 * Fase 13). O conjunto fica num mapa fraco pela mesma chave.
 */
const live: { ref: WeakRef<IUniform<Vector4>>; role: VisualRole; hex: string | undefined }[] = [];
const groups = new WeakMap<IUniform<Vector4>, RoleUniforms>();

/**
 * O acento do mundo (Fase 13, ADR 0026): a cor da classe do jogador LOCAL nos papéis sem cor própria
 * (os interagíveis no chão). Desde a Fase 15 (ADR 0028) o ambiente não tem arestas nem nós: chão, copas,
 * rochas e estruturas ficam cinza. Os outros heróis continuam na cor deles. Sem classe, o neutro.
 */
let worldAccent: string = PALETTE.neutro.hex;

/** A cor de jogo `hex` no modo em uso (a fixa, ou a do modo daltônico). */
export function gameColor(hex: string): string {
  return paletteColor(hex, colorblind);
}

/**
 * A cor de um jogador na tela (F85): a da classe dele no modo em uso. Nunca pelo slot (o slot é só o
 * número); quem repete a classe divide a cor.
 */
export function playerColor(player: { readonly classId: string } | undefined): string {
  return gameColor(classColor(player?.classId ?? ""));
}

/** O jogador com o número `slot` (0 a 3), para o que vem do servidor como número (o `statusBy` − 1). */
export function playerBySlot<P extends { readonly slot: number }>(players: Iterable<P>, slot: number): P | undefined {
  for (const p of players) if (p.slot === slot) return p;
  return undefined;
}

/** Quem guarda a cor de jogo fora dos uniforms de papel (a superfície dos atores, F118). */
export interface GameColorWatcher {
  /** Reescreve a própria cor por `gameColor` (o modo em uso). */
  applyGameColor(): void;
}

const watchers: WeakRef<GameColorWatcher>[] = [];

/** Reescreve `watcher` a cada troca do modo daltônico, enquanto ele viver ou até `unwatchGameColor`. */
export function watchGameColor(watcher: GameColorWatcher): void {
  watchers.push(new WeakRef(watcher));
}

export function unwatchGameColor(watcher: GameColorWatcher): void {
  const i = watchers.findIndex((w) => w.deref() === watcher);
  if (i >= 0) watchers.splice(i, 1);
}

/** O acento do mundo em uso (hex da cor fixa; o modo daltônico entra na escrita). */
export function currentWorldAccent(): string {
  return worldAccent;
}

/**
 * Troca o acento do mundo (a classe do jogador local; `undefined`, o neutro) e reescreve os papéis vivos
 * que o seguem. Sem programa novo: só uniforms.
 */
export function setWorldAccent(hex: string | undefined): void {
  const next = hex ?? PALETTE.neutro.hex;
  if (next === worldAccent) return;
  worldAccent = next;
  rewriteLive((e) => e.hex === undefined);
}

function rewriteLive(only: (e: (typeof live)[number]) => boolean = () => true): void {
  for (let i = live.length - 1; i >= 0; i--) {
    const e = live[i]!;
    const key = e.ref.deref();
    const u = key && groups.get(key);
    if (!u) live.splice(i, 1);
    else if (only(e)) writeRole(u, e.role, e.hex ?? worldAccent);
  }
}

/** Liga ou desliga o modo daltônico e reescreve os acentos vivos (os mortos saem do registro). */
export function setColorblind(on: boolean): void {
  if (on === colorblind) return;
  colorblind = on;
  rewriteLive();
  for (let i = watchers.length - 1; i >= 0; i--) {
    const w = watchers[i]!.deref();
    if (w) w.applyGameColor();
    else watchers.splice(i, 1);
  }
}

/**
 * Uniforms novos (objetos próprios) do papel `role` na cor `accentHex`; sem cor, o papel segue o
 * acento do mundo (Fase 13).
 */
export function roleUniforms(role: VisualRole, accentHex?: string): RoleUniforms {
  const uniforms: RoleUniforms = { uStyleRole: { value: new Vector4() }, uStyleAccent: { value: new Vector4() }, uStyleWhole: { value: 0 } };
  writeRole(uniforms, role, accentHex ?? worldAccent);
  track(uniforms, role, accentHex);
  return uniforms;
}

function track(u: RoleUniforms, role: VisualRole, hex: string | undefined): void {
  const key = u.uStyleAccent;
  groups.set(key, u);
  const i = live.findIndex((e) => e.ref.deref() === key);
  if (i >= 0) live[i] = { ref: live[i]!.ref, role, hex };
  else live.push({ ref: new WeakRef(key), role, hex });
}

function writeRole(u: RoleUniforms, role: VisualRole, accentHex: string): void {
  const r = VISUAL_ROLES[role];
  const accent = new Color(gameColor(accentHex));
  // z livre (era a opacidade das arestas, até a Fase 15).
  u.uStyleRole.value.set(r.gray[0], r.gray[1], 0, r.rim);
  u.uStyleAccent.value.set(accent.r, accent.g, accent.b, r.accent);
  u.uStyleWhole.value = WHOLE.has(role) ? 1 : 0;
}

/** Escreve o papel e a cor do dono nos uniforms de estilo de um material de superfície (sem cor: o acento do mundo). */
export function applyRole(material: ShaderMaterial, role: VisualRole, accentHex?: string): void {
  const u = material.uniforms as unknown as RoleUniforms;
  writeRole(u, role, accentHex ?? worldAccent);
  track(u, role, accentHex);
}
